module.exports = function buildAvoidOverfittingTradingStrategies({ agentProofPoints }) {
  const SOURCE_VERIFIED_DATE = '2026-08-14';

  return {
    path: '/guides/avoid-overfitting-trading-strategies',
    title: 'How to Avoid Overfitting a Trading Strategy | BotSpot',
    description: 'Reduce backtest overfitting by logging every trial, separating strategy selection from evaluation, preserving unseen data, and testing parameter robustness.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'Trading strategy validation guide',
      heading: 'Avoid overfitting a trading strategy before strong backtest results mislead you',
      summary: 'Control the full research process: define the hypothesis and search space first, log every variant, keep strategy selection separate from final evaluation, and prefer stable behavior over one winning configuration.',
      primaryAction: { label: 'Backtest a strategy in BotSpot', url: '/ai-agent' },
      secondaryAction: { label: 'Read the backtesting guide', url: '/guides/backtesting-ai-trading-strategies' },
      proofPoints: agentProofPoints,
      sections: [
        {
          heading: '1. Treat strategy search as part of the backtest',
          paragraphs: [
            'Overfitting does not require an obviously complex model. It can happen when a researcher tries many indicators, parameters, universes, date ranges, filters, or scoring rules and then reports the strongest historical result.',
            'The selected strategy may fit repeatable structure, historical noise, or both. A smooth equity curve cannot distinguish them by itself.',
          ],
          bullets: [
            'Performance depends on one narrow parameter value or date range.',
            'Most gains come from a small number of trades or market episodes.',
            'Small rule changes produce large performance changes.',
            'The strategy performs well only in the sample used to choose it.',
          ],
        },
        {
          heading: '2. Freeze the research protocol before searching',
          paragraphs: [
            'Write the economic or behavioral hypothesis before viewing optimized results. Define what evidence would support it, what evidence would reject it, and which choices the search may change.',
            'Set a research budget. A search with explicit bounds is easier to evaluate than an open-ended loop that stops when an attractive result appears.',
          ],
          bullets: [
            'Declare the asset universe, historical dates, benchmark, and data source.',
            'Choose the primary evaluation metric before ranking variants.',
            'List permitted indicators, parameter ranges, filters, and position-sizing rules.',
            'Set minimum trade count, exposure, liquidity, and risk constraints.',
            'Define stopping criteria and preserve rejected variants in the experiment log.',
          ],
        },
        {
          heading: '3. Separate strategy selection from final evaluation',
          paragraphs: [
            'Use earlier data for development, later data for validation, and a final untouched period for evaluation. Keep every split chronological because random splitting can train on future observations and evaluate on earlier ones.',
            'A holdout period stops being untouched when its result influences another rule, parameter, or feature choice. Move that period into the research record and obtain new evidence instead of continuing to call it a final test.',
            'One holdout is not a complete cure. Research on investment backtests shows that broad strategy selection can still overfit finite historical samples, especially when the same history supports many decisions.',
          ],
          bullets: [
            'Never optimize directly against the final holdout.',
            'Use time-aware splits and an appropriate gap when observations or labels overlap.',
            'Keep data preparation and feature-selection decisions inside the development process.',
            'Record every time validation evidence causes a strategy revision.',
          ],
        },
        {
          heading: '4. Count every trial, including discarded ideas',
          paragraphs: [
            'Testing more alternatives raises the chance that one looks successful through chance. Trial count includes more than explicit parameter combinations: changing universes, date windows, indicators, filters, objectives, or benchmarks also creates selection opportunities.',
            'Record the complete search family. When statistical claims matter, use methods designed for multiple testing or selection bias rather than interpreting the winning unadjusted metric as if it were the only strategy tested.',
          ],
          bullets: [
            'Assign each run an immutable identifier and store its full configuration.',
            'Log failed runs and manual revisions, not only saved strategies.',
            'Report search size and selection rule beside the chosen result.',
            'Distinguish exploratory findings from confirmatory tests.',
            'Use formal corrections only when their assumptions and required inputs are understood.',
          ],
        },
        {
          heading: '5. Prefer stable regions over one parameter peak',
          paragraphs: [
            'Inspect nearby parameter values rather than selecting only the historical maximum. A strategy whose behavior remains similar across a reasonable neighborhood provides stronger robustness evidence than one that works at one exact threshold.',
            'Repeat sensitivity checks across different start dates, subperiods, market conditions, and eligible universes. CFA Institute guidance treats sensitivity and scenario analysis as complements to historical backtesting because one observed history cannot represent every possible future.',
          ],
          bullets: [
            'Plot results across the parameter surface instead of showing one winner.',
            'Compare performance distribution across periods, not only aggregate performance.',
            'Check whether conclusions survive reasonable changes to assumptions.',
            'Investigate why unstable regions fail before adding more parameters.',
          ],
        },
        {
          heading: '6. Reduce unnecessary degrees of freedom',
          paragraphs: [
            'Every additional parameter, filter, exception, or model branch creates another way to fit historical noise. Complexity should earn its place through a stated mechanism and better validation evidence, not only a stronger development backtest.',
            'When a test fails, return to the hypothesis. Repeatedly adding exceptions until the same historical sample passes converts evaluation data into training data.',
          ],
          bullets: [
            'Prefer explicit rules with fewer independently tuned choices.',
            'Remove features whose role cannot be explained or validated.',
            'Compare complex candidates against simple benchmarks.',
            'Require material, stable improvement before retaining extra complexity.',
          ],
        },
        {
          heading: '7. Report evidence without hiding the search process',
          paragraphs: [
            'Publish the hypothesis, data periods, benchmark, primary metric, number of trials, selected configuration, nearby configurations, and limitations. Reporting only the best run hides information needed to judge selection bias.',
            'Move surviving strategies to walk-forward and paper testing. These stages add evidence about changing data and operational behavior, but neither proves future profitability.',
          ],
        },
        {
          heading: 'Limitations and risk',
          paragraphs: [
            'No holdout, cross-validation method, robustness check, or multiple-testing adjustment can guarantee live performance. Financial samples are finite, observations can be dependent, and market structure can change.',
            'Formal measures such as Probability of Backtest Overfitting and Deflated Sharpe Ratio require assumptions, sufficient observations, and a defensible record of the strategies tested. They should support judgment, not become another number to optimize.',
          ],
        },
      ],
      faq: [
        {
          question: 'How many strategy parameters are too many?',
          answer: 'No universal cutoff exists. Risk depends on sample size, parameter ranges, dependence between trials, strategy complexity, and total search breadth. Track every free choice and require each added parameter to improve unseen evidence, not only development results.',
        },
        {
          question: 'Does good out-of-sample performance prove a strategy is not overfit?',
          answer: 'No. It provides stronger evidence only when the sample remained untouched during selection. Repeatedly checking and reacting to the same out-of-sample period turns it into development data.',
        },
        {
          question: 'Should I select the strategy with the highest Sharpe ratio?',
          answer: 'Not by itself. The highest observed Sharpe ratio can reflect selection from many trials, non-normal returns, or dependence on a narrow period. Review search count, robustness, drawdowns, trade behavior, and untouched evaluation evidence.',
        },
        {
          question: 'Can AI optimization increase overfitting risk?',
          answer: 'Yes. AI can generate and test more alternatives faster, increasing selection opportunities. Bound the search, preserve an experiment log, and keep final evaluation data outside the optimization loop.',
        },
      ],
      relatedLinks: [
        { label: 'Backtest AI trading strategies', url: '/guides/backtesting-ai-trading-strategies' },
        { label: 'Understand backtesting metrics', url: '/guides/backtesting-metrics-explained' },
        { label: 'Use walk-forward testing', url: '/guides/walk-forward-testing-trading-strategies' },
        { label: 'Prevent look-ahead bias and data leakage', url: '/guides/look-ahead-bias-and-data-leakage' },
        { label: 'Model slippage, fees, and market impact', url: '/guides/modeling-slippage-fees-and-market-impact' },
      ],
      sources: [
        { label: 'Probability of Backtest Overfitting research', url: 'https://papers.ssrn.com/sol3/papers.cfm?abstract_id=2326253' },
        { label: 'A Reality Check for Data Snooping', url: 'https://doi.org/10.1111/1468-0262.00152' },
        { label: 'Multiple testing in expected-return research', url: 'https://www.nber.org/papers/w20592' },
        { label: 'Deflated Sharpe Ratio research', url: 'https://papers.ssrn.com/sol3/papers.cfm?abstract_id=2460551' },
        { label: 'Model-selection overfitting research', url: 'https://www.jmlr.org/papers/v11/cawley10a.html' },
        { label: 'CFA Institute backtesting and simulation', url: 'https://www.cfainstitute.org/insights/professional-learning/refresher-readings/2026/backtesting-and-simulation' },
        { label: 'scikit-learn time-series cross-validation', url: 'https://scikit-learn.org/stable/modules/generated/sklearn.model_selection.TimeSeriesSplit.html' },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  };
};
